import { useEffect, useState } from "react";
import "./assistant.css";

/**
 * Moods of the mascot. Server moods (happy, sorry, sad, neutral, sleepy) and client moods
 * (thinking while waiting, writing while the person types, wave on greeting, surprised on
 * sudden activity) share the same frame sets.
 */
export type MascotMood =
  | "idle" | "neutral" | "happy" | "thinking" | "sorry" | "sad" | "wave" | "sleepy" | "surprised" | "writing" | "point" | "point-up";

interface FrameSet {
  frames: string[];
  /** ms between frames; 0 = single frame */
  tempo: number;
  /** CSS animation class for the body movement */
  motion: string;
  /** For idle: the second frame is a blink, shown briefly */
  blink?: boolean;
}

const BASE = "/mascot";

const SETS: Record<MascotMood, FrameSet> = {
  idle: { frames: ["idle_a", "idle_b"], tempo: 0, motion: "fs-mascot-breathe", blink: true },
  neutral: { frames: ["idle_a", "idle_b"], tempo: 0, motion: "fs-mascot-breathe", blink: true },
  happy: { frames: ["happy_a", "happy_b"], tempo: 380, motion: "fs-mascot-bounce" },
  thinking: { frames: ["thinking_a", "thinking_b"], tempo: 800, motion: "fs-mascot-sway" },
  sorry: { frames: ["sorry_a", "sorry_b"], tempo: 900, motion: "fs-mascot-shrink" },
  sad: { frames: ["sorry_a", "sorry_b"], tempo: 1300, motion: "fs-mascot-shrink" },
  wave: { frames: ["wave_a", "wave_b"], tempo: 320, motion: "fs-mascot-tilt" },
  sleepy: { frames: ["sleepy_a", "sleepy_b"], tempo: 1500, motion: "fs-mascot-doze" },
  surprised: { frames: ["surprised_a"], tempo: 0, motion: "fs-mascot-pop" },
  writing: { frames: ["writing"], tempo: 0, motion: "fs-mascot-scribble" },
  point: { frames: ["point_a"], tempo: 0, motion: "fs-mascot-nudge" },
  "point-up": { frames: ["point_b"], tempo: 0, motion: "fs-mascot-nudge" },
};

const preloaded = new Set<string>();
/** Warms the browser cache so a mood change never flashes an empty image. */
export function preloadMascot(moods: MascotMood[] = Object.keys(SETS) as MascotMood[]) {
  if (typeof window === "undefined") return;
  for (const mood of moods) {
    for (const f of SETS[mood].frames) {
      if (preloaded.has(f)) continue;
      preloaded.add(f);
      const img = new Image();
      img.src = `${BASE}/${f}.png`;
    }
  }
}

export function mascotFrameUrl(name: string) {
  return `${BASE}/${name}.png`;
}

interface MascotProps {
  mood: MascotMood;
  size?: number;
  className?: string;
  title?: string;
  onClick?: () => void;
  /** Mirror horizontally (the drawings point to the right). */
  flip?: boolean;
}

/** The FillSign chat character, animated by flipping between two drawn frames plus a small CSS motion per mood. */
export function Mascot({ mood, size = 64, className = "", title, onClick, flip = false }: MascotProps) {
  const set = SETS[mood] ?? SETS.idle;
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    setFrame(0);
    if (set.frames.length < 2) return;
    if (set.blink) {
      // Blink: open most of the time, closed for a moment every few seconds (a bit of randomness keeps it alive).
      let closeTimer: number | undefined;
      let openTimer: number | undefined;
      const schedule = () => {
        closeTimer = window.setTimeout(() => {
          setFrame(1);
          openTimer = window.setTimeout(() => { setFrame(0); schedule(); }, 160);
        }, 2600 + Math.random() * 2400);
      };
      schedule();
      return () => { window.clearTimeout(closeTimer); window.clearTimeout(openTimer); };
    }
    const id = window.setInterval(() => setFrame((f) => (f + 1) % set.frames.length), set.tempo);
    return () => window.clearInterval(id);
  }, [set]);

  return (
    <span
      className={`fs-mascot ${set.motion} ${onClick ? "cursor-pointer" : ""} ${className}`}
      style={{ width: size, height: size }}
      title={title}
      onClick={onClick}
      data-mood={mood}
      data-testid="assistant-mascot"
    >
      {set.frames.map((f, i) => (
        <img
          key={f}
          src={mascotFrameUrl(f)}
          alt=""
          draggable={false}
          className="fs-mascot-frame"
          style={{ opacity: i === frame ? 1 : 0, transform: flip ? "scaleX(-1)" : undefined }}
        />
      ))}
    </span>
  );
}
